import React from "react";
import { colors, Typography } from "@commonsku/styles";
import { BASE_COLUMN_TYPES, BaseColumnType } from "../../columns/config/types";
import { ReportColumnConfigBase } from "../../columns/config/ReportColumnConfig";
import { getAbsoluteUrl } from "../../../../utils";

const CreatedColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Date> = {
  header: "Created",
  type: BASE_COLUMN_TYPES.Date,
  fields: ["date_created"],
  accessorKey: "date_created",
  canSort: true,
  orderByField: "date_created",
  width: 110,
};

const ShopColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Link> = {
  header: "Shop",
  type: BASE_COLUMN_TYPES.Link,
  fields: ["shop_number", "shop_name"],
  accessorKey: "shop_number",
  canSort: true,
  orderByField: "shop_name",
  width: 220,
  textOptions: {
    truncate: true,
  },
  dataTransform: ({ shop_number, shop_name }) => ({
    href: shop_number ? `/shop/${shop_number}` : null,
    text: shop_number ? `#${shop_number} - ${shop_name}` : shop_name,
  }),
};

const SupplierColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Text> = {
  header: "Supplier",
  type: BASE_COLUMN_TYPES.Text,
  fields: ["division_name"],
  accessorKey: "division_name",
  canSort: true,
  orderByField: "division_name",
  width: 140,
  textOptions: {
    truncate: true,
  },
};

const ReferenceColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Link> = {
  header: "Reference",
  type: BASE_COLUMN_TYPES.Link,
  fields: ["customer_po", "order_id"],
  accessorKey: "customer_po",
  canSort: true,
  orderByField: "customer_po",
  width: 140,
  dataTransform: ({ customer_po, order_id }) => ({
    href: order_id
      ? getAbsoluteUrl(`public_order.php?order_id=${order_id}`)
      : null,
    text: customer_po,
  }),
};

const CustomerCell = ({ name, email }: { name?: string; email?: string }) => (
  <>
    <Typography.Span style={{ display: "block" }}>{name}</Typography.Span>
    <Typography.Span
      style={{ display: "block", fontSize: 12 }}
      color={colors.neutrals["70"]}
    >
      {email}
    </Typography.Span>
  </>
);

const CustomerColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Generic
> = {
  header: "Customer",
  type: BASE_COLUMN_TYPES.Generic,
  fields: ["checked_out_by", "checked_out_email"],
  accessorKey: "checked_out_by",
  canSort: true,
  orderByField: "checked_out_by",
  width: 220,
  dataTransform: ({ checked_out_by, checked_out_email }) => ({
    cell: <CustomerCell name={checked_out_by} email={checked_out_email} />,
    tooltipText: checked_out_email,
  }),
};

const StatusColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Text> = {
  header: "Status",
  type: BASE_COLUMN_TYPES.Text,
  fields: ["status_name"],
  accessorKey: "status_name",
  canSort: true,
  orderByField: "status_name",
  width: 120,
};

const ValueColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Money> = {
  header: "Value",
  type: BASE_COLUMN_TYPES.Money,
  fields: ["total_subtotal", "currency_id"],
  accessorKey: "total_subtotal",
  canSort: true,
  orderByField: "total_subtotal",
  width: 120,
  moneyOptions: { decimalPlaces: 2 },
  dataTransform: ({ total_subtotal, currency_id }) => ({
    amount: total_subtotal,
    currency_id,
  }),
};

export const Columns: ReportColumnConfigBase<BaseColumnType>[] = [
  CreatedColumn,
  ShopColumn,
  SupplierColumn,
  ReferenceColumn,
  CustomerColumn,
  StatusColumn,
  ValueColumn,
];
